import axios from "axios"
import { type FC, useCallback, useEffect, useState } from "react"
import { Spinner } from "react-bootstrap"
import LDSEllipsis from "src/components/common/LDS/LDSEllipsis/LDSEllipsis"
import AxiosConfig from "src/utilities/AxiosConfig"
import snackBar from "src/utilities/snackBar/SnackBar"
import "./UtilityAuthorization.css"

// Update these IDs to match the EmployeeUtilityAuthorizationStatus seed data
const AUTHORIZED_STATUS_ID = 1
const UNAUTHORIZED_STATUS_ID = 2

interface EmployeeUtilityAuth {
    UtilityID: number
    UtilityName: string
    UtilityAbbr: string
    AuthorizationStatusID: number
}

interface UtilityAuthorizationProps {
    targetedUserId: number
    readOnly: boolean
}

const UtilityAuthorization: FC<UtilityAuthorizationProps> = ({ targetedUserId, readOnly }) => {
    const [utilities, setUtilities] = useState<EmployeeUtilityAuth[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [savingId, setSavingId] = useState<number | null>(null)

    const fetchUtilities = useCallback(() => {
        setLoading(true)
        setError(false)
        axios
            .get(
                `${process.env.REACT_APP_API_URL}/api/user/GetEmployeeUtilityAuthorizations/${targetedUserId}`,
                AxiosConfig.configValue("min")
            )
            .then(res => {
                setUtilities(res.data)
            })
            .catch(() => {
                setError(true)
                snackBar({ text: "Failed to load utility authorizations.", type: "error", timeout: 4000 })
            })
            .finally(() => {
                setLoading(false)
            })
    }, [targetedUserId])

    useEffect(() => {
        fetchUtilities()
    }, [fetchUtilities])

    const handleStatusChange = async (utilityId: number, newStatusId: number) => {
        const prevStatusId = utilities.find(u => u.UtilityID === utilityId)?.AuthorizationStatusID ?? AUTHORIZED_STATUS_ID
        setUtilities(curr => curr.map(u => u.UtilityID === utilityId ? { ...u, AuthorizationStatusID: newStatusId } : u))
        setSavingId(utilityId)
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/user/UpdateEmployeeUtilityAuthorization`,
                { UserId: targetedUserId, UtilityID: utilityId, AuthorizationStatusID: newStatusId },
                AxiosConfig.configValue("min")
            )
            snackBar({ text: "Utility authorization updated.", type: "success", timeout: 3000 })
        } catch {
            setUtilities(curr => curr.map(u => u.UtilityID === utilityId ? { ...u, AuthorizationStatusID: prevStatusId } : u))
            snackBar({ text: "Failed to update utility authorization. Please try again.", type: "error", timeout: 4000 })
        } finally {
            setSavingId(null)
        }
    }

    if (loading) {
        return (
            <div className="UtilityAuthWrapper UtilityAuthCenter">
                <LDSEllipsis color="#7f7f7f" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="UtilityAuthWrapper UtilityAuthCenter">
                <p className="UtilityAuthErrorText">Unable to load utility authorizations.</p>
            </div>
        )
    }

    return (
        <div className="UtilityAuthWrapper">
            <p className="UtilityAuthLabel">Utility Authorization:</p>
            <div className="UtilityAuthList">
                {utilities.length === 0 ? (
                    <p className="UtilityAuthEmpty">No utilities found for this employee.</p>
                ) : utilities.map(utility => (
                    <div key={utility.UtilityID} className="UtilityAuthRow">
                        <span className="UtilityAuthName">
                            {utility.UtilityName}
                            <span className="UtilityAuthAbbr"> ({utility.UtilityAbbr})</span>
                        </span>
                        <div className="UtilityAuthControl">
                            {savingId === utility.UtilityID && (
                                <Spinner animation="border" size="sm" className="UtilityAuthSpinner" />
                            )}
                            <select
                                value={utility.AuthorizationStatusID}
                                disabled={readOnly || savingId !== null}
                                onChange={e => handleStatusChange(utility.UtilityID, Number(e.target.value))}
                                className={`form-control form-control-sm UtilityAuthSelect${utility.AuthorizationStatusID === AUTHORIZED_STATUS_ID ? " authorized" : " unauthorized"}`}
                            >
                                <option value={AUTHORIZED_STATUS_ID}>Authorized</option>
                                <option value={UNAUTHORIZED_STATUS_ID}>Unauthorized</option>
                            </select>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default UtilityAuthorization
